import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  startTime: number;
}

/**
 * Error Boundary for ProductDetail page
 * Catches JavaScript errors, query timeouts, and infinite loops
 */
export class ProductDetailErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 2;
  
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      startTime: Date.now()
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ProductDetail Error Boundary caught an error:', error, errorInfo);
    
    // Check for infinite loop (too many renders in short time)
    const currentTime = Date.now();
    if (currentTime - this.state.startTime < 1000) {
      console.error('⚠️ Potential infinite loop detected in ProductDetail');
    }
    
    this.setState({
      error,
      errorInfo
    });
  }

  handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`🔄 Retrying ProductDetail (attempt ${this.retryCount})`);
      this.setState({ 
        hasError: false, 
        error: undefined, 
        errorInfo: undefined,
        startTime: Date.now()
      });
    } else {
      console.warn('⚠️ Max retries reached for ProductDetail');
      // Navigate back to courses page
      window.location.href = '/courses';
    }
  };

  handleGoBack = () => {
    window.location.href = '/courses';
  };

  render() {
    if (this.state.hasError) {
      const isTimeoutError = this.state.error?.message?.includes('timeout') || 
                           this.state.error?.message?.includes('network');
      
      const isInfiniteLoop = Date.now() - this.state.startTime < 1000;

      return (
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-2xl mx-auto pt-8">
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  페이지 로딩 오류
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant="destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertTitle>
                    {isTimeoutError && '네트워크 연결 오류'}
                    {isInfiniteLoop && '페이지 로딩 중 오류'}
                    {!isTimeoutError && !isInfiniteLoop && '예상하지 못한 오류'}
                  </AlertTitle>
                  <AlertDescription>
                    {isTimeoutError && '네트워크 연결을 확인하고 다시 시도해주세요.'}
                    {isInfiniteLoop && '페이지 로딩 중 문제가 발생했습니다. 새로고침을 시도해주세요.'}
                    {!isTimeoutError && !isInfiniteLoop && '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'}
                  </AlertDescription>
                </Alert>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button 
                    onClick={this.handleRetry}
                    className="flex items-center gap-2"
                    disabled={this.retryCount >= this.maxRetries}
                  >
                    <RefreshCw className={`w-4 h-4 ${this.retryCount >= this.maxRetries ? '' : 'animate-spin-slow'}`} />
                    {this.retryCount >= this.maxRetries ? '재시도 불가' : '다시 시도'}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={this.handleGoBack}
                  >
                    코스 목록으로 돌아가기
                  </Button>
                </div>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                      개발자 정보 (개발 환경에서만 표시)
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </details>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}